import { Settings } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mlm/ui';
import { ProfileTab } from '../components/settings/profile-tab';
import { AddressTab } from '../components/settings/address-tab';
import { PasswordTab } from '../components/settings/password-tab';
import { TwoFactorTab } from '../components/settings/two-factor-tab';
import { KycTab } from '../components/settings/kyc-tab';
import { ReferralCodeTab } from '../components/settings/referral-code-tab';
import { PayoutQrTab } from '../components/settings/payout-qr-tab';
import { PrivacyTab } from '../components/settings/privacy-tab';
import { getCashoutWindow, listPayoutMethods, listPayoutQrs } from '../lib/api';

const TAB_VALUES = [
  'profile',
  'referral',
  'address',
  'password',
  '2fa',
  'kyc',
  'payout-qr',
  'privacy',
];

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab');

  // The Payout QR tab used to be permanent, so every tenant carried a tab for a
  // feature most never turn on. Show it only when a QR is actually wanted —
  // either the tenant-global flag, or some method's own `requiresQr` — or when
  // the member already has one saved (hiding it would strand what they saved).
  // Same query keys as the cash-out page, so TanStack dedupes these.
  const windowQ = useQuery({ queryKey: ['cashout-window'], queryFn: getCashoutWindow });
  const methodsQ = useQuery({ queryKey: ['payout-methods'], queryFn: listPayoutMethods });
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });
  // Wait for all three before deciding, or the tab flashes in and vanishes.
  const qrDecided = windowQ.isSuccess && methodsQ.isSuccess && qrsQ.isSuccess;
  const showPayoutQr =
    qrDecided &&
    (windowQ.data.payoutQrRequired === true ||
      methodsQ.data.some((m) => m.requiresQr) ||
      qrsQ.data.length > 0);

  // A stale bookmark to ?tab=payout-qr must not land on an empty pane. Resolve
  // only once the queries have answered, so we don't bounce a valid deep link
  // during the first render.
  const requested = tab && TAB_VALUES.includes(tab) ? tab : 'profile';
  const activeTab = requested === 'payout-qr' && qrDecided && !showPayoutQr ? 'profile' : requested;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-h1">
          <Settings className="h-6 w-6 text-muted-foreground" /> Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your profile, security, payout verification, and your data.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setParams(
            (p) => {
              p.set('tab', v);
              return p;
            },
            { replace: true },
          )
        }
        className="w-full"
      >
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="referral">Referral</TabsTrigger>
          <TabsTrigger value="address">Address</TabsTrigger>
          <TabsTrigger value="password">Password</TabsTrigger>
          <TabsTrigger value="2fa">Two-factor</TabsTrigger>
          <TabsTrigger value="kyc">KYC</TabsTrigger>
          {showPayoutQr && <TabsTrigger value="payout-qr">Payout QR</TabsTrigger>}
          <TabsTrigger value="privacy">Privacy &amp; data</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="referral">
          <ReferralCodeTab />
        </TabsContent>
        <TabsContent value="address">
          <AddressTab />
        </TabsContent>
        <TabsContent value="password">
          <PasswordTab />
        </TabsContent>
        <TabsContent value="2fa">
          <TwoFactorTab />
        </TabsContent>
        <TabsContent value="kyc">
          <KycTab />
        </TabsContent>
        {showPayoutQr && (
          <TabsContent value="payout-qr">
            <PayoutQrTab />
          </TabsContent>
        )}
        <TabsContent value="privacy">
          <PrivacyTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
